<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';
import { format, parse } from 'date-fns';
import { useToast } from 'vue-toastification';
import { XAlert } from '@clicksign/excalibur';

import { AppTextInput, AppFormValidated } from '~~base-ui';
import { AppFeature, CPF_MASK, DATE_MASK, Decisions, isFetchError, useFeature } from '~~core';

import { fetchSignerUser, saveSignerUser } from '~~/domains/requirement/services/information';

import RequirementAdvanceButton from '../../../../components/RequirementAdvanceButton/RequirementAdvanceButton.vue';
import InformationFormSkeleton from '../../../../components/InformationFormSkeleton/InformationFormSkeleton.vue';
import { useRequirementStep } from '../../../../composables/use-requirement-step';
import { useFetchSigner, signerQueryKeys } from '../../../../repositories/signer';
import { saveRequirement } from '../../../../services';
import { Signer, SignerUser } from '../../../../schemas';

const enum DateFormat {
  DATE_MONTH_YEAR = 'dd/MM/yyyy',
  ISO_8601 = 'yyyy-MM-dd',
}

const STEP_NAME = 'federal_data';

const { t } = useI18n();
const toast = useToast();
const gtm = useGtm();

const DATE_FORMAT_FROM_LOCALE = t('common.dateFormat');

const queryClient = useQueryClient();
const { finishRequirement, signatureRequestKey, setActiveRequirement } = useRequirementStep();

setActiveRequirement(STEP_NAME);

function useReadonlyStatus(signer: Ref<Signer | undefined>) {
  const isNameReadonly = computed(() => !!signer.value?.name);
  const isDocumentationReadonly = computed(() => !!signer.value?.documentation);
  const isBirthdayReadonly = computed(() => !!signer.value?.birthday);
  const isReadonly = computed(() => !!signer.value?.federalDataConfirmed);

  return {
    isNameReadonly,
    isDocumentationReadonly,
    isBirthdayReadonly,
    isReadonly,
  };
}

const { signer, isLoading } = useFetchSigner(signatureRequestKey);
const { isNameReadonly, isDocumentationReadonly, isBirthdayReadonly, isReadonly } = useReadonlyStatus(signer);

// https://github.com/beholdr/maska/issues/149#issuecomment-1529619595
const masks = {
  cpf: CPF_MASK,
  date: DATE_MASK,
};

const formatBirthday = (birthday: string, dateFormat?: DateFormat) => {
  const parsedBirthday = parse(birthday, dateFormat ?? DATE_FORMAT_FROM_LOCALE, new Date());

  return format(parsedBirthday, dateFormat ? DATE_FORMAT_FROM_LOCALE : DateFormat.ISO_8601);
};

function getValidationSchema() {
  const schema: Record<string, string> = {
    fullName: 'required|fullName',
  };

  if (signer.value?.hasDocumentation) {
    schema.documentation = 'required|cpf';
    schema.birthday = `required|date_format:${DATE_FORMAT_FROM_LOCALE}`;
  }

  return schema;
}

const isSubmitting = ref(false);
const signerInfo = ref({
  fullName: '',
  documentation: '',
  birthday: '',
});
const autofilledSignerFields = ref({
  fullName: false,
  documentation: false,
  birthday: false,
});
const showAutofilledAlert = ref(false);
const submitButton = ref<InstanceType<typeof RequirementAdvanceButton>>();

const { isEnabled: isAutofillSignerDataEnabled } = useFeature(Decisions.AUTOFILL_SIGNER_DATA);

const formValidationSchema = computed(() => getValidationSchema());
const hasDocumentation = computed(() => signer.value?.hasDocumentation);
const hasPresetSignerData = computed(() => isNameReadonly.value || isDocumentationReadonly.value || isBirthdayReadonly.value);
const canAutofillSignerData = computed(() => signer.value?.hasUser && !hasPresetSignerData.value);
const autofilledSignerInfo = computed(() => Object.values(autofilledSignerFields.value).some((value) => value));
const alertMessage = computed(() => {
  const filledFields = Object.keys(autofilledSignerFields.value);
  const filteredFilledFields = filledFields.filter((key) => autofilledSignerFields.value[key as keyof typeof autofilledSignerFields.value]);
  const fieldLocale = t(`requirement.information.autofill.fields.${filledFields[0]}`);

  return t('requirement.information.autofill.filled', { count: filteredFilledFields.length, field: fieldLocale });
});

async function saveSignerUserData() {
  // TOGGLE_AUTOFILL_SIGNER_DATA
  // TODO: Remove if and keep inner code
  if (!isAutofillSignerDataEnabled.value) return;

  try {
    const { birthday } = signerInfo.value;
    const formattedBirthday = birthday
      ? formatBirthday(birthday)
      : undefined;

    await saveSignerUser({ ...signerInfo.value, birthday: formattedBirthday });

    toast.success(t('requirement.information.autofill.saved'));

    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_category: 'sign-step-autofill-data',
      ga_event_name: 'success-autosave-data',
    });
  } catch (error) {
    if (!isFetchError(error)) throw error;
  }
}

async function completeRequirement() {
  if (isReadonly.value) return finishRequirement();

  try {
    isSubmitting.value = true;

    const { queryKey } = signerQueryKeys.show(signatureRequestKey.value);

    const payload = {
      name: signerInfo.value.fullName,
      documentation: hasDocumentation.value ? signerInfo.value.documentation : undefined,
      birthday: hasDocumentation.value ? formatBirthday(signerInfo.value.birthday) : undefined,
    };

    if (canAutofillSignerData.value && !showAutofilledAlert.value) await saveSignerUserData();

    await saveRequirement(STEP_NAME, signatureRequestKey.value, payload);
    await queryClient.invalidateQueries(queryKey);

    finishRequirement();
  } catch (error) {
    toast.error(t('error.unexpected'));
    isSubmitting.value = false;
    if (!isFetchError(error)) throw error;
  }
}

function submitClick() {
  submitButton.value?.$el.click();
}

function setAutofillStatus(signerUserData: SignerUser) {
  autofilledSignerFields.value = {
    fullName: !!signerUserData.fullName,
    documentation: !!signerUserData.documentation,
    birthday: !!signerUserData.birthday,
  };

  const allFieldsAutofilled = Object.values(autofilledSignerFields.value).every((value) => value);

  if (autofilledSignerInfo.value) {
    showAutofilledAlert.value = true;
    if (!allFieldsAutofilled) submitClick();
  }
}

async function autofillWithSignerUser() {
  // TOGGLE_AUTOFILL_SIGNER_DATA
  // TODO: Remove if and keep inner code
  if (!isAutofillSignerDataEnabled.value) return;

  try {
    const signerUserData = await fetchSignerUser();

    signerInfo.value = {
      fullName: signerUserData.fullName ?? '',
      documentation: signerUserData.documentation ?? '',
      birthday: signerUserData.birthday ? formatBirthday(signerUserData.birthday, DateFormat.DATE_MONTH_YEAR) : '',
    };

    setAutofillStatus(signerInfo.value);

    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_category: 'sign-step-autofill-data',
      ga_event_name: 'success-autofill-data',
    });
  } catch (error) {
    if (!isFetchError(error)) throw error;
  }
}

function resetAutofilledSignerField(fieldName: 'fullName' | 'birthday') {
  // TOGGLE_AUTOFILL_SIGNER_DATA
  // TODO: Remove if and keep inner code
  if (!isAutofillSignerDataEnabled.value) return;

  autofilledSignerFields.value[fieldName] = false;
  showAutofilledAlert.value = false;
}

watchEffect(async () => {
  if (!isLoading.value) {
    signerInfo.value = {
      fullName: signer.value?.name ?? '',
      documentation: signer.value?.documentation ?? '',
      birthday: signer.value?.birthday ? formatBirthday(signer.value.birthday, DateFormat.ISO_8601) : '',
    };

    if (canAutofillSignerData.value) await autofillWithSignerUser();
  }
});
</script>

<template>
  <div :class="$style.info">
    <div :class="$style.content">
      <header :class="$style.header">
        <h3>{{ $t('requirement.information.title') }}</h3>
      </header>

      <AppFeature
        v-if="showAutofilledAlert"
        :name="Decisions.AUTOFILL_SIGNER_DATA"
      >
        <XAlert
          :class="$style.alert"
          type="info"
          full-width
          small
        >
          {{ alertMessage }}
        </XAlert>
      </AppFeature>

      <InformationFormSkeleton v-if="isLoading" />

      <AppFormValidated
        v-else
        v-slot="{ meta, handleSubmit }"
        :validation-schema="formValidationSchema"
        as="div"
      >
        <form
          ref="form"
          @submit.prevent="handleSubmit($event, () => completeRequirement())"
        >
          <AppTextInput
            v-model="signerInfo.fullName"
            name="fullName"
            full-width
            :class="$style.input"
            :readonly="isNameReadonly"
            :label="$t('requirement.information.form.fullName')"
            @blur="resetAutofilledSignerField('fullName')"
          />

          <AppTextInput
            v-if="hasDocumentation"
            v-model="signerInfo.documentation"
            full-width
            :mask="masks.cpf"
            name="documentation"
            :class="$style.input"
            :readonly="isDocumentationReadonly || autofilledSignerFields.documentation"
            :label="$t('requirement.information.form.documentation')"
            :placeholder="$t('requirement.information.form.documentationPlaceholder')"
            data-testid="documentationInput"
          />

          <AppTextInput
            v-if="hasDocumentation"
            v-model="signerInfo.birthday"
            name="birthday"
            full-width
            :mask="masks.date"
            :class="$style.input"
            :readonly="isBirthdayReadonly"
            :label="$t('requirement.information.form.birthday')"
            :placeholder="$t('requirement.information.form.birthdayPlaceholder')"
            data-testid="birthdayInput"
            @blur="resetAutofilledSignerField('birthday')"
          />

          <AppFeature :name="Decisions.AUTOFILL_SIGNER_DATA">
            <template #default>
              <RequirementAdvanceButton
                ref="submitButton"
                type="submit"
                :loading="isSubmitting"
                full-width
                data-testid="requirementNext"
              />
            </template>

            <template #off>
              <RequirementAdvanceButton
                full-width
                type="submit"
                data-testid="requirementNext"
                :loading="isSubmitting"
                :disabled="meta.dirty && !meta.valid"
              />
            </template>
          </AppFeature>
        </form>
      </AppFormValidated>
    </div>
  </div>
</template>

<style lang="scss" module>
.alert {
  margin-bottom: var(--space-medium);
}

.info {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 14px var(--space-regular) var(--space-large) var(--space-regular);

  .content {
    align-self: center;
    width: 100%;
    max-width: 496px;

    > .header {
      text-align: center;
    }

    .input {
      margin-bottom: var(--space-regular);

      &:last-child {
        margin-bottom: 0;
      }
    }

    .actions {
      margin-top: var(--space-medium);
      margin-bottom: var(--space-regular);

      .button {
        width: 100%;
      }
    }
  }
}
</style>
